import type { JSX } from './jsx-runtime.js'

/** A component intended to run on both server and client. */
export type IslandComponent<P extends Record<string, unknown> = Record<string, unknown>> = (
  props: P,
) => JSX.Element

let counter = 0

/**
 * Mark a component as a client island. Write the component once — the framework
 * compiles it for both server and client automatically.
 *
 * On the server: renders initial HTML and embeds serialized props in `data-props`.
 * On the client: `hydrate()` picks up `[data-island]` elements and remounts the
 * component with full signal reactivity.
 *
 * @example
 * // src/islands/Counter.tsx
 * import { island } from 'davaux'
 * import { createSignal } from 'davaux/client'
 *
 * function Counter({ initial = 0 }: { initial?: number }) {
 *   const [count, setCount] = createSignal(initial)
 *   return <button onClick={() => setCount(c => c + 1)}>{() => count()}</button>
 * }
 * export default island(Counter)
 */
/**
 * Declare a client component as reactive so it can be used in server JSX files
 * without a type error.
 *
 * At runtime this is a pure identity function — the build plugin handles the
 * actual server-side wrapping. The only purpose of `reactive()` is to tell
 * TypeScript that the component's return type is compatible with server JSX
 * (`Promise<string>`) even though the component itself returns a DOM `Node`.
 *
 * @example
 * // src/components/Counter.tsx
 * /** @jsxImportSource davaux/client *\/
 * import { createSignal } from 'davaux/client'
 * import { reactive } from 'davaux'
 *
 * function Counter() {
 *   const [count, setCount] = createSignal(0)
 *   return <button onClick={() => setCount(c => c + 1)}>{() => count()}</button>
 * }
 * export default reactive(Counter)
 */
export function reactive<P>(Component: (props: P) => unknown): (props: P) => Promise<string> {
  return Component as unknown as (props: P) => Promise<string>
}

export function island<P extends Record<string, unknown>>(
  Component: IslandComponent<P>,
  islandId?: string,
): (props: P) => Promise<string> {
  const id = islandId ?? Component.name ?? `island-${counter++}`

  const wrapper = async (props: P): Promise<string> => {
    const rendered = await (Component(props) as unknown as Promise<string> | string)
    const serialized = JSON.stringify(props)
      .replace(/&/g, '\\u0026')
      .replace(/</g, '\\u003c')
      .replace(/>/g, '\\u003e')
      .replace(/'/g, '\\u0027')

    return `<div data-island="${id}" data-props='${serialized}' style="display:contents">${rendered}</div>`
  }
  Object.defineProperty(wrapper, 'name', { value: id, configurable: true })
  return wrapper
}
